import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { MediaFile } from '../types/media';
import { getMediaFiles } from '../services/storage';
import MediaList from '../components/FileUpload/MediaList';
import { factoryList } from '../data/factories';

const CalendarPage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAllFiles();
  }, []);

  const loadAllFiles = async () => {
    setIsLoading(true);
    try {
      const allFiles = await Promise.all(
        factoryList.map(factory => getMediaFiles(factory.id))
      );
      // Trier tous les fichiers par date de création (plus récent en premier)
      const sortedFiles = allFiles.flat().sort((a, b) => b.createdAt - a.createdAt);
      setFiles(sortedFiles);
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  };

  const getFilesForDate = (date: Date) => {
    return files.filter(file => {
      const fileDate = new Date(file.createdAt);
      return isSameDay(fileDate, date);
    }).sort((a, b) => b.createdAt - a.createdAt); // Trier par date décroissante
  };

  const hasFilesForDate = (date: Date) => {
    return getFilesForDate(date).length > 0;
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const weekDays = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDayOfMonth = getFirstDayOfMonth(currentMonth);
  const selectedFiles = getFilesForDate(selectedDate);

  return (
    <div className="animate-fade-in space-y-6 sm:space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Calendar Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 sm:p-4">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={previousMonth}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            </button>
            <h2 className="text-sm font-medium text-gray-900 dark:text-white">
              {currentMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
            </h2>
            <button
              onClick={nextMonth}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1">
            {weekDays.map(day => (
              <div
                key={day}
                className="text-center py-1 text-xs font-medium text-gray-600 dark:text-gray-300"
              >
                {day}
              </div>
            ))}
            
            {Array.from({ length: firstDayOfMonth }).map((_, index) => (
              <div key={`empty-${index}`} className="aspect-square" />
            ))}
            
            {Array.from({ length: daysInMonth }).map((_, index) => {
              const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), index + 1);
              const isSelected = isSameDay(date, selectedDate);
              const hasFiles = hasFilesForDate(date);
              
              return (
                <button
                  key={index}
                  onClick={() => setSelectedDate(date)}
                  className={`aspect-square p-1 rounded-lg text-center relative transition-all duration-200 ${
                    isSelected
                      ? 'bg-primary text-white shadow-md scale-105'
                      : hasFiles
                      ? 'bg-primary/10 dark:bg-primary/20 hover:bg-primary/20 dark:hover:bg-primary/30'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="text-xs font-medium">{index + 1}</span>
                  {hasFiles && (
                    <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2">
                      <CalendarIcon className={`w-2.5 h-2.5 ${
                        isSelected ? 'text-white' : 'text-primary'
                      }`} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Date Files Section */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
            Fichiers du {formatDate(selectedDate)}
          </h2>
          
          <MediaList
            files={selectedFiles}
            isLoading={isLoading}
            onDelete={loadAllFiles}
          />
        </div>
      </div>

      {/* All Files Section - Sorted by date */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
          Tous les fichiers (du plus récent au plus ancien)
        </h2>
        
        <MediaList
          files={files}
          isLoading={isLoading}
          onDelete={loadAllFiles}
        />
      </div>
    </div>
  );
};

export default CalendarPage;